import { createContext, useContext } from "react";
import cn from "./cn";

// The one table styling contract (DESIGN.md › Components › Tables). Real
// <table> markup with scoped heads; `data-ui="table"` opts out of the legacy
// global table restyling in business-ui.css / operations-portal.css.

const DensityContext = createContext("default");

/**
 * TableContainer — the scroll boundary. Horizontal overflow is for genuinely
 * tabular numeric data; on phones most lists should become condensed rows.
 * `maxHeight` makes the head sticky inside a vertically scrolling region.
 */
export function TableContainer({ maxHeight, className, children, label }) {
  return (
    <div
      role={label ? "region" : undefined}
      aria-label={label}
      tabIndex={label ? 0 : undefined}
      className={cn("w-full overflow-x-auto", maxHeight && "overflow-y-auto", className)}
      style={maxHeight ? { maxHeight } : undefined}
    >
      {children}
    </div>
  );
}

export function Table({ density = "default", caption, className, children }) {
  return (
    <DensityContext.Provider value={density}>
      <table data-ui="table" className={cn("w-full border-collapse text-left text-cell text-foreground", className)}>
        {caption ? <caption className="sr-only">{caption}</caption> : null}
        {children}
      </table>
    </DensityContext.Provider>
  );
}

export function THead({ sticky = false, className, children }) {
  return (
    <thead className={cn("bg-surface-sunken", sticky && "sticky top-0 z-[1]", className)}>
      {children}
    </thead>
  );
}

export function TBody({ className, children }) {
  return <tbody className={className}>{children}</tbody>;
}

/** Tr — `interactive` adds the hover wash for rows that open something. */
export function Tr({ interactive = false, selected = false, className, children, ...props }) {
  return (
    <tr
      data-selected={selected || undefined}
      className={cn(
        "border-b border-subtle last:border-b-0",
        interactive && "cursor-pointer hover:bg-hover",
        selected && "bg-accent-subtle",
        className
      )}
      {...props}
    >
      {children}
    </tr>
  );
}

const NESTED_CONTROL = "a, button, input, select, textarea, label, summary, [role='button'], [role='menuitem'], [role='checkbox']";

/**
 * rowOpens — props for a `Tr` that opens a record (DESIGN.md › Rows that open, select or expand). The row is the
 * pointer target; the record's own link inside it stays the keyboard and new-tab target. A click on a nested link,
 * button or field, a modified click, and a click that ends a text selection are left to do their own thing.
 *
 *   <Tr {...rowOpens(() => navigate(path))}> … <Link to={path}>{name}</Link> … </Tr>
 */
export function rowOpens(open) {
  return {
    interactive: true,
    onClick: (event) => {
      if (event.defaultPrevented || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (event.target.closest?.(NESTED_CONTROL)) return;
      if (typeof window !== "undefined" && String(window.getSelection?.() || "")) return;
      open(event);
    },
  };
}

export function Th({ numeric = false, scope = "col", className, children, ...props }) {
  return (
    <th
      scope={scope}
      className={cn(
        "h-8 whitespace-nowrap border-b border-subtle px-4 text-cell-head text-secondary",
        numeric ? "text-right" : "text-left",
        className
      )}
      {...props}
    >
      {children}
    </th>
  );
}

/**
 * Td — `numeric` right-aligns tabular figures; `actions` keeps row actions
 * tight against the right edge.
 */
export function Td({ numeric = false, actions = false, className, children, ...props }) {
  const density = useContext(DensityContext);
  return (
    <td
      className={cn(
        "px-4 align-middle",
        density === "compact" ? "h-row-compact py-1" : "h-row py-2",
        numeric && "text-right tabular-nums",
        actions && "w-px whitespace-nowrap text-right",
        className
      )}
      {...props}
    >
      {children}
    </td>
  );
}

/** A full-width row for empty, error or loading content inside the table. */
export function TableMessageRow({ colSpan, children }) {
  return (
    <tr>
      <td colSpan={colSpan} className="p-0">
        {children}
      </td>
    </tr>
  );
}
